import { useRef } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import '../styles/Solucoes.css'
import { useScrollReveal } from '../shared/hooks/useScrollReveal'

// ── Conteúdo ─────────────────────────────────────────────────────────────────

interface Ramo {
  slug:     string
  nome:     string
  grupo:    string
  icone:    string
  desc:     string
  modulos:  string[] // os mesmos módulos da página de cada ramo
  foto:     string
  w:        number
  h:        number
  alt:      string
  credito?: string
}

const RAMOS: Ramo[] = [
  {
    slug: 'supermercado', nome: 'Supermercado', grupo: 'Varejo',
    icone: '/img/solucoes/icon-supermercado.png',
    desc: 'Controle de estoque, PDV, balanças e integração fiscal completa para supermercados.',
    modulos: ['PDV Rápido', 'Controle de Estoque', 'Integração com Balanças', 'Emissão Fiscal'],
    foto: '/img/solucoes/supermercado/supermarket.jpg', w: 1280, h: 958,
    alt: 'Corredor de supermercado com prateleiras cheias',
  },
  {
    slug: 'autopecas', nome: 'Autopeças', grupo: 'Varejo',
    icone: '/img/solucoes/icon-autopecas.png',
    desc: 'Gestão de estoque de peças, ordens de serviço e controle de garantias.',
    modulos: ['Catálogo de Peças', 'Controle de Estoque', 'Ordens de Serviço', 'Emissão Fiscal'],
    foto: '/img/solucoes/autopecas/auto.jpg', w: 2560, h: 1707,
    alt: 'Mecânico trabalhando embaixo de um carro erguido',
  },
  {
    slug: 'material-de-construcao', nome: 'Material de Construção', grupo: 'Varejo',
    icone: '/img/solucoes/icon-material-construcao.png',
    desc: 'Controle de estoque pesado, entregas, orçamentos e vendas a prazo.',
    modulos: ['Vendas e Orçamentos', 'Controle de Estoque', 'Entregas e Logística', 'Emissão Fiscal'],
    foto: '/img/solucoes/materialdeconstrucao/material.jpg', w: 1024, h: 768,
    alt: 'Corredor de uma loja de material de construção',
  },
  {
    slug: 'distribuidoras-atacadistas', nome: 'Distribuidoras e Atacadistas', grupo: 'Distribuição',
    icone: '/img/solucoes/icon-supermercado.png',
    desc: 'Gestão de rotas, pedidos, tabelas de preços e controle de clientes.',
    modulos: ['Gestão de Pedidos', 'Rotas de Entrega', 'Controle de Estoque', 'Emissão Fiscal'],
    foto: '/img/solucoes/distribuidora/distribuidora.jpg', w: 2560, h: 1703,
    alt: 'Pessoa fechando uma caixa de papelão para envio',
  },
  {
    slug: 'loja-de-roupas', nome: 'Loja de Roupas', grupo: 'Varejo',
    icone: '/img/solucoes/icon-loja-roupas.png',
    desc: 'Controle por grade (cor/tamanho), etiquetas e comissão de vendedores.',
    modulos: ['Controle por Grade', 'Etiquetas e Preços', 'Promoções e Descontos', 'Emissão Fiscal'],
    foto: '/img/solucoes/estoquelojaderoupas.jpg', w: 1024, h: 683,
    alt: 'Camisetas listradas dobradas em uma prateleira',
  },
  {
    slug: 'agropecuaria-e-petshop', nome: 'Agropecuária e Pet Shop', grupo: 'Varejo',
    icone: '/img/solucoes/icon-petshop.png',
    desc: 'Gestão de rações, insumos, agendamento de banho e tosa e controle veterinário.',
    modulos: ['Banho e Tosa', 'Insumos e Rações', 'Controle de Estoque', 'Emissão Fiscal'],
    foto: '/img/solucoes/agropecuariaepetshop/petshop.jpg', w: 2560, h: 1708,
    alt: 'Cliente com um cachorro escolhendo produtos em um pet shop',
  },
  {
    slug: 'papelaria', nome: 'Papelaria', grupo: 'Varejo',
    icone: '/img/solucoes/icon-papelaria.png',
    desc: 'Controle de estoque diversificado, caixa rápido e gestão de recebíveis.',
    modulos: ['PDV Ágil', 'Estoque Diversificado', 'Crediário e Recebíveis', 'Emissão Fiscal'],
    foto: '/img/solucoes/acervo/papelaria-material-escolar.webp', w: 1024, h: 683,
    alt: 'Material escolar sobre uma mesa: aquarela, régua, calculadora e lápis',
    credito: 'Foto: “School Supplies”, rawpixel, CC0',
  },
  {
    slug: 'restaurante', nome: 'Restaurante e Lanchonete', grupo: 'Alimentação',
    icone: '/img/solucoes/icon-restaurante.png',
    desc: 'PDV de mesa, comanda eletrônica, controle de ingredientes e cozinha.',
    modulos: ['PDV de Mesa e Comanda', 'Controle de Cozinha', 'Controle de Ingredientes', 'Emissão Fiscal'],
    foto: '/img/solucoes/restaurante/restaurante.jpg', w: 2560, h: 1707,
    alt: 'Atendente sorrindo atrás do balcão de um café',
  },
  {
    slug: 'posto-de-combustivel', nome: 'Posto de Combustível', grupo: 'Serviços',
    icone: '/img/solucoes/icon-posto-combustivel.png',
    desc: 'Controle de bombas, lubrificantes, loja de conveniência e frota.',
    modulos: ['Controle de Bombas', 'Loja de Conveniência', 'Lubrificantes e Serviços', 'Emissão Fiscal'],
    foto: '/img/solucoes/postodecombustivel/posto.jpg', w: 2560, h: 1707,
    alt: 'Frentista abastecendo um carro em um posto',
  },
  {
    slug: 'engenharia', nome: 'Engenharia e Construção', grupo: 'Serviços',
    icone: '/img/solucoes/icon-engenharia.png',
    desc: 'Gestão de obras, medições, contratos e controle de materiais.',
    modulos: ['Gestão de Obras', 'Contratos e Medições', 'Controle de Materiais', 'Emissão Fiscal'],
    foto: '/img/solucoes/engenhariaeconstrucao/engeconstrucao.jpeg', w: 950, h: 633,
    alt: 'Engenheiros conferindo uma planta em um canteiro de obras',
  },
  {
    slug: 'distribuidora-de-agua', nome: 'Distribuidora de Água', grupo: 'Distribuição',
    icone: '/img/solucoes/icon-dist-agua.png',
    desc: 'Controle de garrafões, rotas de entrega e gestão de clientes recorrentes.',
    modulos: ['Controle de Garrafões', 'Rotas de Entrega', 'Clientes Recorrentes', 'Emissão Fiscal'],
    foto: '/img/solucoes/distribuidoradeagua/distribuidora.jpg', w: 1200, h: 630,
    alt: 'Garrafões de água empilhados prontos para entrega',
  },
  {
    slug: 'e-commerce', nome: 'E-commerce', grupo: 'Online',
    icone: '/img/solucoes/icon-ecommerce.png',
    desc: 'Integração com plataformas online, controle de pedidos e estoque.',
    modulos: ['Integração com Lojas', 'Gestão de Pedidos', 'Logística e Envio', 'Emissão Fiscal'],
    foto: '/img/solucoes/ecommerce/ecommerce.jpg', w: 1200, h: 628,
    alt: 'Notebook aberto com caixas de papelão ao lado',
  },
  {
    slug: 'otica', nome: 'Ótica', grupo: 'Varejo',
    icone: '/img/solucoes/icon-otica.png',
    desc: 'Ficha de clientes com grau de óculos, controle de armações e lentes.',
    modulos: ['Ficha do Cliente', 'Armações e Lentes', 'Crediário e Convênios', 'Emissão Fiscal'],
    foto: '/img/solucoes/otica/otica.jpg', w: 2560, h: 1706,
    alt: 'Cliente experimentando óculos com a ajuda de uma atendente',
  },
]

const BASE = [
  {
    titulo: 'Frente de Caixa',
    desc: 'PDV rápido com leitor de código de barras, várias formas de pagamento e fechamento de caixa por operador.',
    foto: '/img/solucoes/supermercado/MARKET01.png', w: 740, h: 390,
    alt: 'Operadora de caixa registrando compras',
  },
  {
    titulo: 'Controle de Estoque',
    desc: 'Entrada pela nota do fornecedor, inventário e alerta de estoque mínimo para não faltar produto.',
    foto: '/img/solucoes/estoque.webp', w: 1216, h: 640,
    alt: 'Conferente com um tablet entre prateleiras de estoque',
  },
  {
    titulo: 'Financeiro',
    desc: 'Contas a pagar e receber, crediário e relatórios gerenciais para acompanhar o caixa da empresa.',
    foto: '/img/contabilidade.jpg', w: 1024, h: 576,
    alt: 'Mãos usando calculadora e teclado ao lado de papéis',
  },
  {
    titulo: 'Emissão Fiscal',
    desc: 'NF-e, NFC-e e SPED com transmissão automática para a SEFAZ e atualizações sem custo extra.',
    foto: '/img/solucoes/entrada.webp', w: 689, h: 420,
    alt: 'Ilustração de uma nota fiscal eletrônica',
  },
]

const INCLUSO = [
  'Implantação e treinamento inclusos',
  'Suporte técnico presencial e remoto',
  'Atualizações fiscais sem custo extra',
  'Funciona online e offline',
]

// ── Página ───────────────────────────────────────────────────────────────────

export default function Solucoes() {
  useScrollReveal()

  const [params, setParams] = useSearchParams()
  const ativo = RAMOS.find(r => r.slug === params.get('ramo')) ?? RAMOS[0]
  const cartaoRef = useRef<HTMLDivElement>(null)

  function selecionar(ramo: Ramo) {
    setParams({ ramo: ramo.slug }, { replace: true })
    // No celular a lista fica acima do cartão: leva o visitante até ele
    if (window.matchMedia('(max-width: 900px)').matches) {
      const suave = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      cartaoRef.current?.scrollIntoView({ behavior: suave ? 'smooth' : 'auto', block: 'start' })
    }
  }

  return (
    <div className="sol">

      {/* ══ HERO ══ */}
      <section className="sol-hero">
        <div className="sol-hero-grid" aria-hidden="true" />
        <div className="sol-hero-inner">
          <div className="sol-hero-texto">
            <div className="sol-hero-eyebrow">Soluções por segmento</div>
            <h1 className="sol-hero-title">
              Feito para o<br />
              <em>seu negócio</em>
            </h1>
            <p className="sol-hero-sub">
              O <span translate="no">Quantum</span> vem configurado para o seu ramo, com as funções que o dia a dia
              de cada negócio pede, do supermercado ao posto de combustível.
            </p>
            <div className="sol-hero-btns">
              <Link to="/agendar-visita" className="sol-btn-red">Agendar Demonstração</Link>
              <a href="#ramos" className="sol-btn-outline">Ver os Ramos</a>
            </div>
          </div>

          <div className="sol-hero-fotos">
            <img className="sol-hero-foto sol-hero-foto--alta" src="/img/solucoes/postodecombustivel/posto.jpg" width={2560} height={1707} alt="Frentista abastecendo um carro em um posto" {...({ fetchpriority: 'high' } as Record<string, string>)} />
            <img className="sol-hero-foto" src="/img/solucoes/supermercado/supermarket.jpg" width={1280} height={958} alt="Corredor de supermercado" />
            <img className="sol-hero-foto" src="/img/solucoes/otica/otica.jpg" width={2560} height={1706} alt="Atendente de ótica ajustando óculos em um cliente" />
            <div className="sol-hero-selo">
              <strong>20+</strong>
              <span>anos atendendo diferentes ramos</span>
            </div>
          </div>
        </div>
      </section>

      {/* ══ ESCOLHA DO RAMO ══ */}
      <section className="sol-section sol-ramos" id="ramos">
        <div className="sol-section-inner">
          <div className="sol-section-header" data-sr data-delay="0">
            <span className="sol-eyebrow">Escolha o seu ramo</span>
            <h2 className="sol-section-title">Veja o que o <span translate="no">Quantum</span> faz no seu negócio</h2>
            <p className="sol-section-sub">
              Selecione um ramo para ver as funções específicas e acessar a página completa da solução.
            </p>
          </div>

          <div className="sol-seletor" data-sr data-delay="80">
            <div className="sol-lista" role="group" aria-label="Ramos atendidos">
              {RAMOS.map(r => {
                const selecionado = r.slug === ativo.slug
                return (
                  <button
                    key={r.slug}
                    type="button"
                    className={`sol-item${selecionado ? ' is-ativo' : ''}`}
                    aria-pressed={selecionado}
                    aria-controls="sol-cartao"
                    onClick={() => selecionar(r)}
                  >
                    <span className="sol-item-icone">
                      <img src={r.icone} width={80} height={65} alt="" />
                    </span>
                    <span className="sol-item-nome">{r.nome}</span>
                    <i className="fa-solid fa-chevron-right sol-item-seta" aria-hidden="true" />
                  </button>
                )
              })}
            </div>

            <div className="sol-cartao" id="sol-cartao" ref={cartaoRef}>
              <figure className="sol-cartao-foto" key={ativo.slug}>
                <img src={ativo.foto} width={ativo.w} height={ativo.h} alt={ativo.alt} loading="lazy" decoding="async" />
                {ativo.credito && <figcaption>{ativo.credito}</figcaption>}
              </figure>
              <div className="sol-cartao-corpo">
                <span className="sol-eyebrow">{ativo.grupo}</span>
                <h3 className="sol-cartao-titulo">{ativo.nome}</h3>
                <p className="sol-cartao-desc">{ativo.desc}</p>
                <ul className="sol-checklist">
                  {ativo.modulos.map(m => (
                    <li key={m}>
                      <i className="fa-solid fa-circle-check" aria-hidden="true" />
                      {m}
                    </li>
                  ))}
                </ul>
                <div className="sol-cartao-btns">
                  <Link to={`/solucoes/${ativo.slug}`} className="sol-btn-red">
                    Ver Solução Completa
                    <i className="fa-solid fa-arrow-right" aria-hidden="true" />
                  </Link>
                  <Link to="/agendar-visita" className="sol-btn-blue-outline">Agendar Demonstração</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ BASE COMUM ══ */}
      <section className="sol-section sol-base">
        <div className="sol-section-inner">
          <div className="sol-section-header" data-sr data-delay="0">
            <span className="sol-eyebrow">Incluso em todos os ramos</span>
            <h2 className="sol-section-title">A mesma base completa em todo negócio</h2>
            <p className="sol-section-sub">
              Seja qual for o seu ramo, o sistema já vem com o essencial da gestão integrado.
            </p>
          </div>
          <div className="sol-base-grid">
            {BASE.map((b, i) => (
              <div key={b.titulo} className="sol-base-card" data-sr data-delay={String(i * 90)}>
                <img src={b.foto} width={b.w} height={b.h} alt={b.alt} loading="lazy" decoding="async" />
                <div className="sol-base-corpo">
                  <h3 className="sol-base-titulo">{b.titulo}</h3>
                  <p className="sol-base-desc">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ IMPLANTAÇÃO ══ */}
      <section className="sol-section sol-equipe">
        <div className="sol-section-inner sol-equipe-grid">
          <div data-sr data-delay="0">
            <span className="sol-eyebrow">Implantação e suporte</span>
            <h2 className="sol-equipe-titulo">Nossa equipe coloca o sistema para funcionar com você</h2>
            <Link to="/quem-somos" className="sol-btn-red-outline">Conheça a Quantum</Link>
          </div>
          <div data-sr data-delay="120">
            <p className="sol-equipe-p">
              A equipe Quantum configura o sistema para o seu ramo, treina quem vai usar o caixa
              e continua por perto depois, com atendimento humanizado em todo o Brasil.
            </p>
            <ul className="sol-checklist sol-checklist--escura">
              {INCLUSO.map(item => (
                <li key={item}>
                  <i className="fa-solid fa-circle-check" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section className="sol-cta">
        <div className="sol-cta-marca" aria-hidden="true">QUANTUM</div>
        <div className="sol-cta-inner" data-sr data-delay="0">
          <span className="sol-cta-eyebrow">Não encontrou o seu ramo?</span>
          <h2 className="sol-cta-titulo">O <span translate="no">Quantum</span> se adapta ao seu negócio</h2>
          <p className="sol-cta-sub">
            O sistema pode ser configurado para outros tipos de empresa. Fale com a nossa equipe e
            veja o Quantum funcionando no seu negócio.
          </p>
          <div className="sol-cta-btns">
            <Link to="/agendar-visita" className="sol-btn-white">Falar com um Consultor</Link>
            <a href="https://wa.me/5563984042819" target="_blank" rel="noreferrer" className="sol-btn-outline">
              <i className="fa-brands fa-whatsapp" aria-hidden="true" />
              (63)&nbsp;98404-2819
            </a>
          </div>
        </div>
      </section>

    </div>
  )
}
